import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import MenuBar from '../../components/menuBar/menuBar';
import PageFooter from '../../components/pageFooter/pageFooter';
import styles from '../../publicAsset/termsOfServicePage.module.scss';
import ContactConstant from '../../utils/Constant/ContactConstant';

const SECTIONS = [
    { id: 'information-collect', label: '1. Information We Collect' },
    { id: 'how-we-use', label: '2. How We Use Your Information' },
    { id: 'sharing', label: '3. How We Share Your Information' },
    { id: 'data-retention', label: '4. Data Retention' },
    { id: 'your-rights', label: '5. Your Rights' },
    { id: 'cookies', label: '6. Cookies and Tracking' },
    { id: 'children', label: "7. Children's Privacy" },
    { id: 'data-security', label: '8. Data Security' },
    { id: 'international', label: '9. International Data Transfers' },
    { id: 'changes', label: '10. Changes to This Policy' },
    { id: 'contact', label: '11. Contact Us' },
];

const PrivacyPolicyPage = () => {
    const [activeSection, setActiveSection] = useState('introduction');

    // Scroll-driven fixed sidebar — position:sticky breaks when html/body has overflow-x:hidden
    const heroRef = useRef(null);
    const sidebarWrapperRef = useRef(null);
    const sidebarRef = useRef(null);
    const containerRef = useRef(null);
    const [isFixed, setIsFixed] = useState(false);
    const [fixedStyle, setFixedStyle] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const update = () => {
            if (!heroRef.current || !sidebarWrapperRef.current || !containerRef.current || !sidebarRef.current) return;
            const heroBottom = heroRef.current.getBoundingClientRect().bottom;
            const wrapperRect = sidebarWrapperRef.current.getBoundingClientRect();
            const containerBottom = containerRef.current.getBoundingClientRect().bottom;
            const sidebarHeight = sidebarRef.current.offsetHeight;
            const pastHero = heroBottom <= 88;
            const beforeFooter = containerBottom > sidebarHeight + 88 + 12;
            if (pastHero && beforeFooter) {
                setIsFixed(true);
                setFixedStyle({
                    position: 'fixed',
                    top: 88,
                    left: wrapperRect.left,
                    width: wrapperRect.width,
                });
            } else {
                setIsFixed(false);
                setFixedStyle({});
            }
        };
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
        );
        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 90;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <div className={styles.page}>
            <Helmet>
                <title>Privacy Policy | Prepal</title>
                <meta name="description" content="Read Prepal's Privacy Policy to understand how we collect, use, and protect your personal information on our AI-powered test preparation platform." />
                <meta name="keywords" content="Prepal privacy policy, data protection, PDPA, personal data, privacy" />
                <meta name="author" content="Prepal Team" />
            </Helmet>

            <MenuBar />

            {/* Hero Banner */}
            <div className={styles.hero} ref={heroRef}>
                <div className={styles.heroContent}>
                    <p className={styles.heroEyebrow}>Legal</p>
                    <h1 className={styles.heroTitle}>Privacy Policy</h1>
                    <p className={styles.heroSubtitle}>
                        prepal (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; &quot;our&quot;) is committed to protecting your privacy and handling your data responsibly. This Privacy Policy explains what information we collect, how we use it, who we share it with, and your rights regarding your personal data when you use the prepal platform (the &quot;Service&quot;). prepal is currently operating as a pre-incorporation entity.
                    </p>
                    <p className={styles.heroMeta}>Effective Date: <strong>March 5, 2026</strong></p>
                </div>
            </div>

            {/* Main Layout */}
            <div className={styles.container} ref={containerRef}>
                {/* Wrapper always holds flex space; inner aside goes fixed via JS */}
                <div ref={sidebarWrapperRef} className={styles.sidebarWrapper}>
                    <aside
                        ref={sidebarRef}
                        className={`${styles.sidebar} ${isFixed ? styles.sidebarFixed : ''}`}
                        style={fixedStyle}
                    >
                        <div className={styles.tocCard}>
                            <p className={styles.tocHeading}>Table of Contents</p>
                            <nav>
                                <ul className={styles.tocList}>
                                    {SECTIONS.map(({ id, label }) => (
                                        <li key={id}>
                                            <button
                                                className={`${styles.tocItem} ${activeSection === id ? styles.tocItemActive : ''}`}
                                                onClick={() => scrollToSection(id)}
                                            >
                                                {label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>
                    </aside>
                </div>

                {/* Content */}
                <main className={styles.content}>

                    <p className={styles.text}>By using the Service, you agree to the collection and use of information in accordance with this policy. This policy should be read alongside our Terms of Service.</p>

                    <section id="information-collect" className={styles.section}>
                        <h2 className={styles.sectionTitle}>1. Information We Collect</h2>

                        <h3 className={styles.subSectionTitle}>1.1 Information You Provide Directly</h3>
                        <ul className={styles.list}>
                            <li><strong>Account Information:</strong> When you register, we collect your name, email address, and password. If a parent or guardian creates an account on behalf of a minor, we collect the parent/guardian's contact information as well.</li>
                            <li><strong>Profile Information:</strong> Goal score, target test date, current grade level, and any other information you choose to provide in your profile settings.</li>
                            <li><strong>Payment Information:</strong> When you make a purchase, payment is processed by our third-party payment processor, Lemon Squeezy. We do NOT store your credit card number, bank account details, or other financial account information on our servers. We receive only a transaction confirmation, amount paid, and billing country from Lemon Squeezy.</li>
                            <li><strong>Communications:</strong> When you contact us via email at {ContactConstant.INFO_EMAIL} or through in-app feedback, we collect the content of those communications.</li>
                        </ul>

                        <h3 className={styles.subSectionTitle}>1.2 Information Generated by Your Use of the Service</h3>
                        <ul className={styles.list}>
                            <li><strong>Learning Data:</strong> Your answers to practice questions, time spent per question, hint usage, questions flagged or bookmarked, and chatbot conversation history within study sessions.</li>
                            <li><strong>Performance Data:</strong> Scores, difficulty ratings, topic-level performance metrics, session completion rates, and adaptive algorithm outputs (estimated skill levels).</li>
                            <li><strong>Usage Data:</strong> Features accessed, session duration, frequency of use, pages visited within the app, and device/browser type.</li>
                        </ul>

                        <h3 className={styles.subSectionTitle}>1.3 Information Collected Automatically</h3>
                        <ul className={styles.list}>
                            <li><strong>Log Data:</strong> IP address, browser type, operating system, referring URLs, and timestamps of access.</li>
                            <li><strong>Cookies and Similar Technologies:</strong> We use essential cookies to maintain your session and authenticate your login. We do not use advertising cookies or third-party tracking cookies. See Section 6 for details.</li>
                        </ul>
                    </section>

                    <section id="how-we-use" className={styles.section}>
                        <h2 className={styles.sectionTitle}>2. How We Use Your Information</h2>
                        <p className={styles.text}>We use the information we collect for the following purposes:</p>

                        <h3 className={styles.subSectionTitle}>2.1 To Provide and Improve the Service</h3>
                        <ul className={styles.list}>
                            <li>Deliver adaptive practice questions tailored to your skill level using Item Response Theory (IRT)</li>
                            <li>Generate instant explanations through our chatbot feature</li>
                            <li>Track and display your progress across SAT topics</li>
                            <li>Process your payment and manage your account access</li>
                            <li>Provide customer support and respond to your inquiries</li>
                        </ul>

                        <h3 className={styles.subSectionTitle}>2.2 To Improve Our Algorithms and Content</h3>
                        <ul className={styles.list}>
                            <li><strong>Aggregated and Anonymized Data:</strong> We use anonymized learning data (stripped of personal identifiers) to improve our adaptive algorithms, refine question difficulty calibration, and develop new features. No individual user can be identified from this aggregated data.</li>
                            <li><strong>Internal Analytics:</strong> We analyze usage patterns to understand which features are most valuable, identify bugs or friction points, and prioritize our product roadmap.</li>
                        </ul>

                        <h3 className={styles.subSectionTitle}>2.3 To Communicate With You</h3>
                        <ul className={styles.list}>
                            <li>Send account-related notifications (purchase confirmation, password resets)</li>
                            <li>Send product updates about new features available to you</li>
                            <li>Send study reminders and progress summaries (which you can opt out of at any time)</li>
                        </ul>
                        <p className={styles.text}><strong>We will never sell your email address to third parties or send you unrelated marketing communications.</strong></p>
                    </section>

                    <section id="sharing" className={styles.section}>
                        <h2 className={styles.sectionTitle}>3. How We Share Your Information</h2>
                        <p className={styles.text}>We do not sell your personal data. We share information only in the following limited circumstances:</p>

                        <h3 className={styles.subSectionTitle}>3.1 Third-Party Service Providers</h3>
                        <p className={styles.text}>We use the following third-party services to operate prepal. Each processes data only as necessary to provide their service to us:</p>
                        <ul className={styles.list}>
                            <li><strong>Lemon Squeezy (Payment Processing):</strong> Processes your payment as Merchant of Record. Receives your payment method details, billing address, and email. Lemon Squeezy's privacy policy governs their handling of your payment data. We do not have access to your full payment details.</li>
                            <li><strong>OpenAI (Explanation Generation):</strong> Powers the chatbot explanations. When you ask a question, the text of your query and relevant question context is sent to OpenAI's API. We have configured this integration so that your inputs are NOT used to train OpenAI's models. OpenAI processes data as a sub-processor under their API data usage policy.</li>
                            <li><strong>Supabase (Database &amp; Authentication):</strong> Stores your account information, learning data, and authentication credentials. Data is encrypted at rest and in transit.</li>
                            <li><strong>Railway (Hosting):</strong> Hosts our frontend application. Processes standard web request data (IP address, browser information) as part of serving the application.</li>
                        </ul>

                        <h3 className={styles.subSectionTitle}>3.2 Legal Requirements</h3>
                        <p className={styles.text}>We may disclose your information if required to do so by Vietnamese law, or in response to valid legal process (court order, government investigation) from competent authorities in Vietnam.</p>

                        <h3 className={styles.subSectionTitle}>3.3 Business Transfers</h3>
                        <p className={styles.text}>If prepal is involved in a merger, acquisition, or sale of assets, your personal data may be transferred as part of that transaction. We will notify you via email and/or a prominent notice on the Service before your data is transferred and becomes subject to a different privacy policy.</p>
                    </section>

                    <section id="data-retention" className={styles.section}>
                        <h2 className={styles.sectionTitle}>4. Data Retention</h2>
                        <p className={styles.text}>We retain your personal data for as long as your account is active or as needed to provide you with the Service.</p>
                        <ul className={styles.list}>
                            <li><strong>Account Data:</strong> Retained for the duration of your active account. If you request account deletion, we will delete your personal data within 30 days, except where retention is required by Vietnamese law (e.g., financial transaction records).</li>
                            <li><strong>Learning Data:</strong> Anonymized learning data (with personal identifiers removed) may be retained indefinitely for algorithm improvement purposes, as this data cannot be linked back to you.</li>
                            <li><strong>Payment Records:</strong> Transaction records are retained for a minimum of 5 years as required by Vietnamese tax and accounting regulations.</li>
                            <li><strong>Support Communications:</strong> Retained for 2 years after the last interaction for quality assurance and legal compliance.</li>
                        </ul>
                    </section>

                    <section id="your-rights" className={styles.section}>
                        <h2 className={styles.sectionTitle}>5. Your Rights</h2>
                        <p className={styles.text}>Under Vietnamese data protection laws and our commitment to transparency, you have the following rights:</p>
                        <ul className={styles.list}>
                            <li><strong>Right to Access:</strong> You can request a copy of the personal data we hold about you.</li>
                            <li><strong>Right to Correction:</strong> You can request that we correct any inaccurate or incomplete personal data.</li>
                            <li><strong>Right to Deletion:</strong> You can request that we delete your account and personal data, subject to legal retention requirements.</li>
                            <li><strong>Right to Object:</strong> You can object to our processing of your data for specific purposes, including opting out of non-essential communications.</li>
                            <li><strong>Right to Data Portability:</strong> You can request an export of your learning data in a commonly used format.</li>
                        </ul>
                        <p className={styles.text}>To exercise any of these rights, contact us at {ContactConstant.INFO_EMAIL}. We will respond to your request within 15 working days.</p>
                    </section>

                    <section id="cookies" className={styles.section}>
                        <h2 className={styles.sectionTitle}>6. Cookies and Tracking</h2>

                        <h3 className={styles.subSectionTitle}>6.1 Cookies We Use</h3>
                        <ul className={styles.list}>
                            <li><strong>Essential/Session Cookies:</strong> Required for the Service to function. These maintain your login session and remember your preferences within a session. These cannot be disabled without breaking the Service.</li>
                            <li><strong>Analytics Cookies:</strong> We use basic analytics to understand how users interact with the platform (pages visited, features used, session duration). This data is used solely for product improvement and is not shared with advertisers.</li>
                        </ul>

                        <h3 className={styles.subSectionTitle}>6.2 Cookies We Do NOT Use</h3>
                        <ul className={styles.list}>
                            <li>Advertising or retargeting cookies</li>
                            <li>Third-party social media tracking pixels</li>
                            <li>Cross-site tracking cookies</li>
                        </ul>
                        <p className={styles.text}>You can control cookies through your browser settings. Disabling essential cookies may prevent you from using the Service.</p>
                    </section>

                    <section id="children" className={styles.section}>
                        <h2 className={styles.sectionTitle}>7. Children's Privacy</h2>
                        <p className={styles.text}>
                            prepal is designed for SAT preparation and may be used by students aged 15 and above (the age of consent in Vietnam). Users under 15 require parental or guardian consent to create an account.
                        </p>
                        <p className={styles.text}>We take additional care with data from users under 18:</p>
                        <ul className={styles.list}>
                            <li>We do not serve targeted advertising to any users, including minors</li>
                            <li>We do not sell or share minor data with third parties for marketing purposes</li>
                            <li>Parents or guardians can request access to, correction of, or deletion of their child's data at any time by contacting {ContactConstant.INFO_EMAIL}</li>
                            <li>If we learn that we have collected personal data from a child under 15 without parental consent, we will take steps to delete that information promptly.</li>
                        </ul>
                    </section>

                    <section id="data-security" className={styles.section}>
                        <h2 className={styles.sectionTitle}>8. Data Security</h2>
                        <p className={styles.text}>We implement appropriate technical and organizational measures to protect your personal data:</p>
                        <ul className={styles.list}>
                            <li>All data is encrypted in transit using TLS/SSL (HTTPS)</li>
                            <li>Database data is encrypted at rest</li>
                            <li>Access to personal data is restricted to authorized team members only</li>
                            <li>We use secure authentication practices including password hashing</li>
                            <li>We conduct regular security reviews of our infrastructure</li>
                        </ul>
                        <p className={styles.text}>While we strive to protect your data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security but will notify you promptly in the event of any data breach affecting your personal information.</p>
                    </section>

                    <section id="international" className={styles.section}>
                        <h2 className={styles.sectionTitle}>9. International Data Transfers</h2>
                        <p className={styles.text}>Your data may be processed by third-party services (listed in Section 3) whose servers are located outside of Vietnam, including in the United States. When data is transferred internationally, we ensure that appropriate safeguards are in place, including contractual obligations on our service providers to protect your data in accordance with this policy.</p>
                    </section>

                    <section id="changes" className={styles.section}>
                        <h2 className={styles.sectionTitle}>10. Changes to This Policy</h2>
                        <p className={styles.text}>We may update this Privacy Policy from time to time. When we make material changes, we will:</p>
                        <ul className={styles.list}>
                            <li>Update the &quot;Effective Date&quot; at the top of this document</li>
                            <li>Notify you via email or through a prominent notice on the Service</li>
                            <li>Where required by law, seek your consent before applying changes to your existing data</li>
                        </ul>
                        <p className={styles.text}>We encourage you to review this policy periodically.</p>
                    </section>

                    <section id="contact" className={styles.section}>
                        <h2 className={styles.sectionTitle}>11. Contact Us</h2>
                        <p className={styles.text}>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
                        <div className={styles.contactCard}>
                            <div className={styles.contactRow}>
                                <span className={styles.contactLabel}>Company</span>
                                <span className={styles.contactValue}>prepal</span>
                            </div>
                            <div className={styles.contactRow}>
                                <span className={styles.contactLabel}>Email</span>
                                <a href={`mailto:${ContactConstant.INFO_EMAIL}`} className={styles.contactValue}>{ContactConstant.INFO_EMAIL}</a>
                            </div>
                            <div className={styles.contactRow}>
                                <span className={styles.contactLabel}>Website</span>
                                <a href="https://studyprepal.com/privacy" className={styles.contactValue}>https://studyprepal.com/privacy</a>
                            </div>
                            <div className={styles.contactRow}>
                                <span className={styles.contactLabel}>Location</span>
                                <span className={styles.contactValue}>Ho Chi Minh City, Vietnam</span>
                            </div>
                        </div>
                        <p className={styles.text}>For urgent privacy concerns, we aim to respond within 5 working days. For formal rights requests (Section 5), we will respond within 15 working days.</p>
                    </section>

                    <div className={styles.bottomBar}>
                        <p>© {new Date().getFullYear()} prepal. All rights reserved.</p>
                        <div className={styles.bottomLinks}>
                            <Link to="/landing" className={styles.bottomLink}>Home</Link>
                            <Link to="/terms-of-service" className={styles.bottomLink}>Terms of Service</Link>
                            <Link to="/pricing" className={styles.bottomLink}>Pricing</Link>
                        </div>
                    </div>

                </main>
            </div>

            <PageFooter />
        </div>
    );
};

export default PrivacyPolicyPage;
